import React, { useMemo } from 'react';
import './BrandPicker.scss';
import { useTranslation } from 'react-i18next';
import type { InsightsBrandFunnelRow } from '@brand24-original/llm-types';
import CheckboxSelect, {
  CheckboxSelectOption,
} from '../../../../shared/components/checkbox-select/CheckboxSelect';
import BrandIcon from '../../../../shared/components/brand-icon/BrandIcon';
import { brandColorOf } from '../../../../shared/components/report/brandColors';

interface BrandPickerProps {
  brands: InsightsBrandFunnelRow[];
  focusBrandKey: string;
  selectedKeys: string[];
  /** Report brand-color map from `useBrandSelection` — the same one the matrix headers use. */
  brandColors: Map<string, string>;
  /** Receives the full new list (like ModelSelect); the own brand is always in it. */
  onChange: (brandKeys: string[]) => void;
}

/**
 * Brand selector on the shared `CheckboxSelect` — the same dropdown as the model filter in the panel.
 * The trigger always reads "Brands" (the selection count is visible on the visualization); the rows
 * use the shared `BrandIcon` in report brand colors, so a brand looks the same here as in the matrix
 * headers. Any number of brands may be ticked (user decision — the former cap of 10 is gone; 10 is
 * only the default selection size). The own brand is `fixed` (ticked, not un-tickable, not dimmed — CR
 * decision). Selection state lives in useBrandSelection at the caller.
 */
const BrandPicker: React.FC<BrandPickerProps> = ({
  brands,
  focusBrandKey,
  selectedKeys,
  brandColors,
  onChange,
}) => {
  const { t } = useTranslation();

  const options = useMemo<CheckboxSelectOption<string>[]>(
    () =>
      brands.map((brand, index) => {
        const isFocus = brand.brandKey === focusBrandKey;
        return {
          value: brand.brandKey,
          label: brand.label,
          fixed: isFocus,
          content: (
            <div className="dropdown-item brand-picker__item">
              <BrandIcon
                name={brand.label}
                color={brandColorOf(brandColors, brand.label, isFocus, index)}
                size="xs"
              />
              <span className={`brand-picker__name${isFocus ? ' brand-picker__name--own' : ''}`}>
                {brand.label}
              </span>
            </div>
          ),
        };
      }),
    [brands, focusBrandKey, brandColors]
  );

  return (
    <CheckboxSelect
      selected={selectedKeys}
      onChange={onChange}
      options={options}
      triggerContent={
        <span className="checkbox-select__label">{t('insightPage.brandPicker.trigger')}</span>
      }
      maxListHeight={280}
      className="brand-picker"
    />
  );
};

export default BrandPicker;
